import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import api from '../../api';
import { JobSheetLabel } from '../../labels';
import SoloPartImage from '../../labels/JobSheetLabel/SoloPartImage';
import array from '../../utils/array';
import { ENVIRONMENT, PART_TYPES, NON_PART_BKG_COLOR, PART_BKG_COLOR } from '../../constants';
import DuoPartImage from '../../labels/JobSheetLabel/DuoPartImage';

const PartGrid = ({ partChunk }) => {
    const sx = {
        grid: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr' },
        partCtnr: { height: '2.3in', width: '3.3in', borderTop: '1px solid black', borderRight: '1px solid black' },
        partCount: (nonPart) => ({ fontSize: '13px', float: 'right', fontWeight: 'bolder', height: '15px', paddingLeft: '2px', paddingRight: '2px', color: nonPart ? 'white' : 'black', backgroundColor: nonPart ? NON_PART_BKG_COLOR : PART_BKG_COLOR }),
        imgCtnr: { height: '2.1in', width: '3.3in', margin: '0 auto' },
    };

    return (
        <div style={sx.grid}>
            {partChunk.map((part, idx) => {
                const nonPart = part?.type == PART_TYPES.NON_PART;
                return (
                    <div key={idx} style={sx.partCtnr}>
                        <div style={sx.partCount(nonPart)}>
                            {(part?.type == PART_TYPES.PART ? 'Part' : 'Non-Part')} {part?.partNo} of {part?.partTotalNo}
                        </div>
                        <div style={sx.imgCtnr}>
                            {ENVIRONMENT != 'production' && <div style={{ position: 'absolute', zIndex: 999, fontWeight: 'bolder', color: 'white', backgroundColor: 'black', padding: '2px' }}>{part?.id}</div>}
                            <SoloPartImage part={part} />
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

const PartSplitScreen = ({ drawing, layout }) => {
    const sx = {
        grid: { display: 'grid', gridTemplateColumns: '1fr 1fr' },
        ctnr: { width: '5in', height: '6.9in' }
    };

    return (
        <div style={sx.grid}>
            {[drawing, layout].map((part, idx) => (
                <div key={idx} style={sx.ctnr}>
                    <DuoPartImage part={part} />
                </div>))}
        </div>
    );
};

const JobSheet = ({ printRef, jobId, setLoaded, colorBand, checklist, part, hashId, template, options }) => {
    const [areas, setAreas] = useState([]);
    const [job, setJob] = useState({});

    // load bins
    useEffect(async () => {
        const { area, job } = await api.jobs.getSheet({ jobId }) || {};
        setAreas(area);
        setJob(job);
        setLoaded(true);
    }, [jobId]);

    const labelProps = { colorBand, checklist, hashId, job, part, template };

    return (
        <div ref={printRef} style={{ display: 'none' }}>
            {areas.map(({ areaName, drawing, layout, parts, nonParts, filter } = {}, idx) => {

                const activeParts = [...options?.parts ? parts : [], ...options?.nonParts ? nonParts : []]; // spread into active parts depending on options

                const drawingOnly = (drawing && !layout) || (drawing && !options?.layouts && options?.drawings); // if drawing exists and layout doesn't or if drawing exists and layouts is disabled and drawings is enabled
                const layoutOnly = (layout && !drawing) || (layout && !options?.drawings && options?.layouts); // if layout exists and drawing doesn't or if layout exists and drawings is disabled and layouts is enabled

                return (
                    <div key={idx}>

                        {/* show split screen layout/drawing if both exist and are enabled */}
                        {(drawing && layout && options?.layouts && options?.drawings) && (
                            <JobSheetLabel nonPart
                                areaType='drawing / layout'
                                key={`${idx}-drawing-layout`}
                                image={<PartSplitScreen drawing={drawing} layout={layout} />}
                                areaPartsLength={parts.length}
                                {...{ ...labelProps, areaName, filter }}
                            />
                        )}

                        {/* show the drawing if the layout doesn't exist or it's disabled */}
                        {drawingOnly && (
                            <JobSheetLabel
                                areaType='drawing'
                                key={`${idx}-drawing`}
                                image={<SoloPartImage fullSize part={drawing} />}
                                areaPartsLength={parts.length}
                                {...{ ...labelProps, areaName, filter }}
                            />
                        )}

                        {/* show the layout if the drawing doesn't exist or it's disabled */}
                        {layoutOnly && (
                            <JobSheetLabel
                                areaType='layout'
                                key={`${idx}-layout`}
                                image={<SoloPartImage fullSize part={layout} />}
                                areaPartsLength={parts.length}
                                {...{ ...labelProps, areaName, filter }}
                            />
                        )}


                        {/* active parts (non-parts possibly included) */}
                        {(activeParts.length > 0) && array.chunk(activeParts, 9).map((partChunk, idx) => (
                            <JobSheetLabel
                                areaType='parts'
                                key={`${idx}-parts`}
                                image={<PartGrid partChunk={partChunk} />}
                                {...{ ...labelProps, areaName, filter }}
                            />
                        ))}

                    </div>
                );
            })}
        </div>
    );
};

PartSplitScreen.propTypes = {
    drawing: PropTypes.object,
    layout: PropTypes.object
};

PartGrid.propTypes = {
    partChunk: PropTypes.array
};

JobSheet.propTypes = {
    printRef: PropTypes.object,
    jobId: PropTypes.string,
    setLoaded: PropTypes.func,
    filter: PropTypes.object,
    colorBand: PropTypes.array,
    checklist: PropTypes.object,
    part: PropTypes.object,
    hashId: PropTypes.string,
    template: PropTypes.object,
    options: PropTypes.object
};

export default JobSheet;